import { useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { notification as antdNotif } from 'antd';
import { useAuthStore } from '@/store/authStore';
import type { Notification } from '@/types';

/**
 * Kết nối SignalR hub `/hubs/notifications` của backend.
 * - Tự động reconnect
 * - Nhận sự kiện "ReceiveNotification" → invalidate React Query cache + hiển thị toast
 * - Nhận sự kiện "UnreadCountChanged" → update badge
 */
export function useNotificationsHub() {
  const { token, isAuthenticated } = useAuthStore();
  const qc = useQueryClient();
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api';
    const hubUrl = baseUrl.replace(/\/api\/?$/, '') + '/hubs/notifications';

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { accessTokenFactory: () => token })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Incoming events
    connection.on('ReceiveNotification', (notif: Notification) => {
      // Hiện toast ngắn
      antdNotif.open({
        message: notif.title,
        description: notif.content,
        placement: 'topRight',
        duration: 5,
        onClick: () => {
          if (notif.link) window.location.href = notif.link;
        },
      });
      // Refresh danh sách + count
      qc.invalidateQueries({ queryKey: ['notifications'] });
    });

    connection.on('UnreadCountChanged', (count: number) => {
      qc.setQueryData(['notifications', 'unread-count'], count);
    });

    connection.start().catch((err) => {
      // Không fail ứng dụng nếu hub không có — BE có thể chưa chạy
      console.warn('[SignalR] Không kết nối được hub:', err.message);
    });

    connectionRef.current = connection;

    return () => {
      connection.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [isAuthenticated, token, qc]);

  return connectionRef.current;
}
